import { captureException } from '@sentry/react';
import { EmptyStatePrimaryAction } from '@synoptic/ui-kit/empty-state/empty-state-primary-action.js';
import { EmptyState } from '@synoptic/ui-kit/empty-state/empty-state.js';
import { EmojiSadIcon } from '@synoptic/ui-kit/icons/react/emoji-sad.js';
import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';
import { isClientNetworkFailure } from '@/utils/is-network-error.ts';
import {
  reloadStaleRouteManifest,
  shouldReloadStaleRouteManifest,
} from '@/utils/stale-route-manifest.ts';
import { errorBlock } from './page.css';

export const ErrorBanner = ({
  primaryAction = (
    <EmptyStatePrimaryAction
      onClick={() => {
        location.reload();
      }}
    >
      Reload
    </EmptyStatePrimaryAction>
  ),
  secondaryAction,
}: {
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const error = useRouteError() as any;
  const reloadStaleManifest = shouldReloadStaleRouteManifest(error);

  useEffect(() => {
    if (reloadStaleManifest) reloadStaleRouteManifest();
  }, [reloadStaleManifest]);

  if (
    !reloadStaleManifest &&
    error instanceof Error &&
    !isClientNetworkFailure(error)
  ) {
    captureException(error);
  }

  let description =
    error?.statusText || error?.message || error?.name || 'Unknown error';

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      description = 'Page not found';
    }
  }

  return (
    <div>
      <EmptyState
        illustration={<EmojiSadIcon />}
        heading="Something went wrong"
        description={
          <div>
            Please reload the page. If it doesn’t help, send this text to us:
            <pre className={errorBlock}>{description}</pre>
          </div>
        }
        action={primaryAction}
        secondaryAction={secondaryAction}
      />
    </div>
  );
};
